import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    UploadCloud, FileArchive, CheckCircle2, AlertCircle, Trash2, ArrowLeft,
    Database, FileText, Download, Loader2, Calendar, Wand2,
} from "lucide-react";
import { toast } from "sonner";

const ENTITY_LABELS = {
    chart_of_accounts: "Chart of Accounts",
    customers: "Customers",
    vendors: "Vendors",
    items: "Items & Services",
    taxes: "Tax Rates",
    currencies: "Currencies",
    invoices: "Invoices",
    invoice_lines: "Invoice Line Items",
    estimates: "Estimates",
    estimate_lines: "Estimate Line Items",
    sales_orders: "Sales Orders",
    delivery_challans: "Delivery Challans",
    credit_notes: "Credit Notes",
    credit_note_lines: "Credit Note Line Items",
    payments_received: "Payments Received",
    recurring_invoices: "Recurring Invoices",
    bills: "Bills",
    bill_lines: "Bill Line Items",
    purchase_orders: "Purchase Orders",
    vendor_credits: "Vendor Credits",
    payments_made: "Payments Made",
    recurring_bills: "Recurring Bills",
    bank_accounts: "Bank Accounts",
    bank_transactions: "Bank Transactions",
    manual_journals: "Manual Journals",
    manual_journal_lines: "Manual Journal Lines",
    stock_adjustments: "Stock Adjustments",
    stock_transfers: "Stock Transfers",
    fixed_assets: "Fixed Assets",
};

export default function ZohoImport() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [step, setStep] = useState(1); // 1=upload, 2=review, 3=committing, 4=done
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [analysis, setAnalysis] = useState(null);
    const [job, setJob] = useState(null);
    const [committing, setCommitting] = useState(false);
    const [results, setResults] = useState(null);
    const [fromDate, setFromDate] = useState("2024-04-01");
    const [toDate, setToDate] = useState("");
    const [selected, setSelected] = useState({});
    const [recentJobs, setRecentJobs] = useState([]);
    const [dragActive, setDragActive] = useState(false);

    useEffect(() => { loadJobs(); }, []);

    const loadJobs = async () => {
        try {
            const { data } = await api.get("/zoho/jobs");
            setRecentJobs(data || []);
        } catch { /* ignore */ }
    };

    const handleFiles = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".zip")) {
            toast.error("Please upload your Zoho Books backup as a .zip file");
            return;
        }
        setUploading(true);
        setProgress(0);
        const form = new FormData();
        form.append("file", file);
        try {
            const token = localStorage.getItem("vyastra_token");
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${API}/zoho/upload`);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
            };
            xhr.onload = () => {
                if (xhr.status === 200) {
                    const data = JSON.parse(xhr.responseText);
                    setAnalysis(data);
                    setJob({ id: data.job_id, filename: data.filename });
                    // Default-select all detected entities
                    const sel = {};
                    (data.summary_by_entity || []).forEach((e) => { sel[e.entity] = true; });
                    setSelected(sel);
                    setStep(2);
                    toast.success(`Analyzed ${data.files_detected.length} files`);
                    loadJobs();
                } else {
                    let msg = "Upload failed";
                    try { msg = JSON.parse(xhr.responseText).detail || msg; } catch { /* ignore */ }
                    toast.error(msg);
                }
                setUploading(false);
            };
            xhr.onerror = () => { toast.error("Upload failed"); setUploading(false); };
            xhr.send(form);
        } catch (e) {
            toast.error("Upload failed");
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleFiles(f);
    };

    const commit = async (dryRun = false) => {
        if (!job) return;
        const entities = Object.keys(selected).filter((k) => selected[k]);
        if (!entities.length) return toast.error("Pick at least one entity to import");
        setCommitting(true);
        try {
            const { data } = await api.post(`/zoho/jobs/${job.id}/commit`, {
                entities,
                from_date: fromDate || null,
                to_date: toDate || null,
                dry_run: dryRun,
            });
            setResults(data);
            setStep(dryRun ? 2 : 4);
            if (!dryRun) loadJobs();
            toast.success(dryRun ? "Dry run complete" : "Import acknowledged");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Commit failed");
        } finally { setCommitting(false); }
    };

    const reset = () => {
        setAnalysis(null); setJob(null); setResults(null); setStep(1); setSelected({}); setProgress(0);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const deleteJob = async (jid) => {
        if (!confirm("Delete this import job and its uploaded ZIP?")) return;
        try {
            await api.delete(`/zoho/jobs/${jid}`);
            toast.success("Deleted");
            loadJobs();
        } catch { toast.error("Delete failed"); }
    };

    return (
        <>
            <PageHeader
                kicker="DATA MIGRATION"
                title="Import from Zoho Books"
                subtitle="Bring your full ledger across — chart of accounts, customers, vendors, invoices, bills, payments, and more — in one go."
                breadcrumbs={
                    <button onClick={() => navigate("/settings")} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                        <ArrowLeft className="w-3.5 h-3.5" />Settings
                    </button>
                }
            />
            <PageBody>
                <Stepper step={step} />

                {step === 1 && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            <div
                                onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                                onDragLeave={() => setDragActive(false)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={onDrop}
                                onClick={() => fileInputRef.current?.click()}
                                className={`border-2 border-dashed rounded-md p-12 text-center cursor-pointer transition-colors ${dragActive ? "border-gold bg-gold/5" : "border-border hover:bg-muted/40"}`}
                                data-testid="zoho-dropzone"
                            >
                                <UploadCloud className="w-10 h-10 mx-auto text-muted-foreground" strokeWidth={1.4} />
                                <div className="mt-4 font-display text-xl font-light">Drop your Zoho Books backup ZIP here</div>
                                <div className="text-[12.5px] text-muted-foreground mt-1">or click to browse</div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".zip,application/zip"
                                    className="hidden"
                                    onChange={(e) => handleFiles(e.target.files?.[0])}
                                    data-testid="zoho-file-input"
                                />
                                {uploading && (
                                    <div className="mt-4">
                                        <div className="text-[12px] text-muted-foreground mb-1">Uploading… {progress}%</div>
                                        <div className="h-1.5 bg-muted rounded overflow-hidden">
                                            <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} />
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 text-[12px] text-muted-foreground">
                                <p className="mb-1"><b className="text-foreground">How to get your Zoho backup:</b></p>
                                <ol className="list-decimal pl-5 space-y-0.5">
                                    <li>Sign in to Zoho Books → top-right gear icon → Settings</li>
                                    <li>Under "Data Administration", click <b>Data Backup</b></li>
                                    <li>Select all modules → click <b>Backup Data</b> → download the ZIP</li>
                                    <li>Drop that ZIP here. We'll detect the contents and preview before committing.</li>
                                </ol>
                            </div>
                        </div>

                        <div>
                            <div className="bg-card border border-border rounded-md p-5">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">RECENT IMPORTS</div>
                                {recentJobs.length === 0 ? (
                                    <div className="text-[12.5px] text-muted-foreground">No previous imports.</div>
                                ) : (
                                    <div className="space-y-2">
                                        {recentJobs.map((j) => (
                                            <div key={j.id} className="border border-border rounded-md p-3 text-[12.5px]" data-testid={`zoho-job-${j.id}`}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <FileArchive className="w-4 h-4 text-muted-foreground shrink-0" />
                                                        <span className="truncate font-medium">{j.filename}</span>
                                                    </div>
                                                    <button onClick={(e) => { e.stopPropagation(); deleteJob(j.id); }} className="text-muted-foreground hover:text-destructive">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                                <div className="text-[11px] text-muted-foreground mt-1">{(j.files_detected || []).length} files • {Math.round((j.size_bytes || 0) / 1024)} KB • {j.status}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {step === 2 && analysis && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 bg-card border border-border rounded-md p-6">
                            <div className="flex items-center justify-between border-b border-border pb-4 mb-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">ANALYSIS</div>
                                    <div className="font-display text-xl font-light mt-0.5 truncate max-w-md">{analysis.filename}</div>
                                </div>
                                <Button variant="outline" size="sm" onClick={reset} data-testid="zoho-reset"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Discard</Button>
                            </div>

                            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Detected modules ({(analysis.summary_by_entity || []).length})</div>
                            <table className="fin-table">
                                <thead><tr><th style={{ width: 32 }}></th><th>Module</th><th className="num">Files</th><th className="num">Rows</th></tr></thead>
                                <tbody>
                                    {(analysis.summary_by_entity || []).map((e) => (
                                        <tr key={e.entity} data-testid={`zoho-entity-${e.entity}`}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={!!selected[e.entity]}
                                                    onChange={(ev) => setSelected((s) => ({ ...s, [e.entity]: ev.target.checked }))}
                                                    data-testid={`zoho-select-${e.entity}`}
                                                />
                                            </td>
                                            <td>
                                                <div className="font-medium">{ENTITY_LABELS[e.entity] || e.entity}</div>
                                                <div className="text-[11px] text-muted-foreground font-mono">{e.files.join(", ")}</div>
                                            </td>
                                            <td className="num">{e.files.length}</td>
                                            <td className="num">{e.total_rows.toLocaleString("en-IN")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {(analysis.files_unknown || []).length > 0 && (
                                <details className="mt-5 text-[12.5px]">
                                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                        Unrecognized files ({analysis.files_unknown.length}) — will be skipped
                                    </summary>
                                    <ul className="mt-2 ml-4 space-y-1 text-[11.5px] text-muted-foreground">
                                        {analysis.files_unknown.map((u) => (
                                            <li key={u.filename} className="font-mono">{u.filename} <span className="italic">— {u.reason}</span></li>
                                        ))}
                                    </ul>
                                </details>
                            )}

                            {results && (
                                <div className="mt-6 border-t border-border pt-5">
                                    <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Last commit result {results.dry_run && "(dry run)"}</div>
                                    <div className="space-y-1 text-[12.5px]">
                                        {(results.results || []).map((r) => (
                                            <div key={r.entity} className="flex items-center gap-2">
                                                {r.status === "skipped" ? <AlertCircle className="w-3.5 h-3.5 text-amber-600" /> : <CheckCircle2 className="w-3.5 h-3.5 text-positive" />}
                                                <span className="font-medium">{ENTITY_LABELS[r.entity] || r.entity}</span>
                                                <span className="text-muted-foreground">— {r.status} {r.total_source_rows ? `· ${r.total_source_rows} rows` : ""}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div className="bg-card border border-border rounded-md p-5">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">CUTOVER WINDOW</div>
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">From date</Label>
                                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1.5" data-testid="zoho-from-date" />
                                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground mt-3 block">To date (optional)</Label>
                                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1.5" data-testid="zoho-to-date" />
                                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                                    Transactions before this date will be skipped. Master data (customers, vendors, items, COA) is always imported in full.
                                </p>
                            </div>

                            <div className="bg-card border border-border rounded-md p-5">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-3">ACTIONS</div>
                                <div className="space-y-2">
                                    <Button variant="outline" className="w-full justify-start" onClick={() => commit(true)} disabled={committing} data-testid="zoho-dry-run">
                                        <Wand2 className="w-4 h-4 mr-2" />Run dry-run preview
                                    </Button>
                                    <Button className="w-full justify-start" onClick={() => commit(false)} disabled={committing} data-testid="zoho-commit">
                                        {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                                        Commit import
                                    </Button>
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                                    <span className="text-amber-700 font-medium">Phase 1 mode:</span> The wizard will recognize every module and prepare the import job. Per-entity commit logic activates in Phase 2 once we've inspected your CSV column headers — that prevents data corruption from guessing column names.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {step === 4 && results && (
                    <div className="bg-card border border-border rounded-md p-8 text-center max-w-2xl mx-auto">
                        <CheckCircle2 className="w-12 h-12 mx-auto text-positive" strokeWidth={1.4} />
                        <div className="font-display text-2xl font-light mt-4">Import job acknowledged</div>
                        <p className="text-[13px] text-muted-foreground mt-2 max-w-md mx-auto">
                            Your Zoho backup has been parsed and queued. Phase 2 mappers will be wired in shortly — once active, the data will flow into your books exactly as you reviewed.
                        </p>
                        <div className="mt-6 flex items-center justify-center gap-2">
                            <Button variant="outline" onClick={reset}>Import another</Button>
                            <Button onClick={() => navigate("/")} data-testid="zoho-go-dashboard">Back to dashboard</Button>
                        </div>
                    </div>
                )}
            </PageBody>
        </>
    );
}

function Stepper({ step }) {
    const steps = [
        { n: 1, label: "Upload" },
        { n: 2, label: "Review & Map" },
        { n: 4, label: "Done" },
    ];
    return (
        <div className="flex items-center gap-3 text-[11.5px] uppercase tracking-[0.14em] text-muted-foreground mb-6">
            {steps.map((s, i) => (
                <div key={s.n} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-medium ${step >= s.n ? "bg-gold text-white" : "bg-muted text-muted-foreground"}`}>
                        {s.n === 4 && step >= 4 ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.n}
                    </div>
                    <span className={step >= s.n ? "text-foreground font-medium" : ""}>{s.label}</span>
                    {i < steps.length - 1 && <div className="w-8 h-px bg-border" />}
                </div>
            ))}
        </div>
    );
}
